import React, { useMemo } from 'react';
import { IITH } from '@/pages/landing/iithBrand';
import FamilyPersonCard from './FamilyPersonCard';

const LINE = IITH.gold;
const LINE_W = 2;

/** Vertical stem under a node. */
function Stem({ h = 28 }) {
  return (
    <div
      className="mx-auto shrink-0"
      style={{ width: LINE_W, height: h, background: LINE }}
      aria-hidden
    />
  );
}

/**
 * Classic org-chart fork: stem → crossbar → one dropper per child.
 * Width tracks the card row so lines land under each card center.
 */
function TreeFork({ count }) {
  if (count <= 0) return null;
  if (count === 1) return <Stem h={36} />;

  return (
    <div className="relative w-full max-w-3xl mx-auto" aria-hidden>
      <Stem h={24} />
      <div className="relative mx-auto" style={{ width: '100%', maxWidth: count * 180 }}>
        <div
          className="absolute left-[12.5%] right-[12.5%] top-0"
          style={{ height: LINE_W, background: LINE }}
        />
        <div className="flex justify-between">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="flex flex-1 justify-center">
              <div style={{ width: LINE_W, height: 28, background: LINE }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * You at the top — dependents branch below.
 * Keeps rich cards; clear gold stems/forks (no hearts).
 */
export default function FamilyTreeDiagram({ self, members = [] }) {
  const dependents = useMemo(
    () => (members || []).filter((m) => m.id !== self?.id),
    [self, members],
  );

  if (!self) return null;

  return (
    <div
      className="relative overflow-hidden rounded-3xl border border-[#D5DEE8] shadow-sm"
      style={{
        background:
          `radial-gradient(ellipse 70% 45% at 50% 0%, ${IITH.saffron}10 0%, transparent 55%),` +
          ` linear-gradient(180deg, #FBFCFD 0%, #FFFFFF 45%)`,
      }}
      data-testid="patient-family-tree"
    >
      <div className="relative flex flex-col items-center px-4 py-10 sm:px-10">
        <p className="mb-4 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-[#C4A35A]">
          You
        </p>
        <FamilyPersonCard person={self} you featured accent={IITH.saffron} />

        {dependents.length > 0 ? (
          <>
            <TreeFork count={dependents.length} />
            <p className="mb-4 mt-1 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-[#C4A35A]">
              Dependents
            </p>
            <div className="flex w-full max-w-3xl flex-wrap items-start justify-center gap-4 sm:gap-5">
              {dependents.map((m) => (
                <FamilyPersonCard key={m.id} person={m} accent={IITH.navyMid} />
              ))}
            </div>
          </>
        ) : (
          <p className="mt-8 text-sm text-[#7A8B9C]">No dependents linked on this phone yet.</p>
        )}

        <p className="mt-8 max-w-md text-center text-[11px] text-[#7A8B9C]">
          Linked on the same registered phone. Tap anyone to open their full care history.
        </p>
      </div>
    </div>
  );
}
